import { IUser, UserFields } from '../../modules/models/user';
import { IFormRules, ruleMap, validateRules } from '../../utils/useValidator';
import { validateAddress, validateRequiredAddress } from '../../utils/addressUtils';
import { isEmpty, isNull } from '../../utils/generalUtils';
import { validateBadge } from './projectRules';
import { isValidNumber } from 'libphonenumber-js';
import { validateObject } from 'utils/formUtils';
import { ClientModel, GeneralModel, ResourceModel } from 'modules/models';
import { validateBadgeTagId } from 'utils/projectUtils';
import { IWorkerProfileField } from 'modules/models/workerProfile';
import { CompanyType } from 'modules/models/client';

const validateUBadgePrice = value => validateBadge(value, 1000, true) && 'Universal Badge Price must be less than $1,000.';

export const userRules: IFormRules = {
  firstName: {
    required: true,
    rules: [],
  },
  lastName: {
    required: true,
    rules: [],
  },
  email: {
    required: true,
    rules: [ruleMap.isValidEmail],
  },
  title: {
    required: false,
    rules: [],
  },
  otherTrade: {
    required: false,
    rules: [],
  },
  invitationType: {
    required: false,
    rules: [],
  },
  mobilePhoneNumber: {
    required: false,
    rules: [({ value }) => value && !isValidNumber(value) && 'Please enter a valid Mobile Phone Number.'],
  },
  officePhoneNumber: {
    required: false,
    rules: [({ value }) => value && !isValidNumber(value) && 'Please enter a valid Office Phone Number.'],
  },
  officePhoneExtension: {
    required: false,
    rules: [],
  },
  preferredContactMethod: {
    required: true,
    rules: [],
  },
  projectRoleId: {
    required: true,
    rules: [],
  },
  tagId: {
    required: false,
    rules: [validateBadgeTagId],
  },
};

export const userActiveRules = ({ assignClientRequired = true, groupIdsRequired = false }): IFormRules => ({
  ...userRules,
  firstName: {
    ...userRules.firstName,
    required: true,
  },
  lastName: {
    ...userRules.lastName,
    required: true,
  },
  email: {
    ...userRules.email,
    required: true,
  },
  assignClient: {
    required: assignClientRequired,
    rules: [],
  },
  companyUserAccessData: {
    required: false,
    rules: [validateCompanyUserAccessData],
  },
  companyUserImpersonateAccessList: {
    required: false,
    rules: [validateCompanyUserImpersonateAccessList],
  },
  projectRoleId: {
    required: true,
    rules: [],
  },
  groupIds: {
    required: groupIdsRequired || false,
    rules: [],
  },
  projects: { required: false, rules: [] },
  tagId: {
    required: false,
    rules: [validateBadgeTagId],
  },
});

export const fcaUserActiveRules = ({ assignClientRequired = true, groupIdsRequired = false }): IFormRules => ({
  ...userRules,
  firstName: {
    ...userRules.firstName,
    required: true,
  },
  lastName: {
    ...userRules.lastName,
    required: true,
  },
  email: {
    ...userRules.email,
    required: true,
  },
  assignClient: {
    required: assignClientRequired,
    rules: [],
  },
  groupIds: {
    required: groupIdsRequired || false,
    rules: [],
  },
  projects: { required: false, rules: [] },
  tagId: {
    required: false,
    rules: [validateBadgeTagId],
  },
  projectRoleId: {
    required: false,
    rules: [],
  },
});

export const validateCompanyUserAccessData = ({ value }) => {
  if (value && value.companyGroupProjects && value.companyGroupProjects.length) {
    let result = {};
    value.companyGroupProjects
      .filter(cgp => !cgp.groupId || cgp.groupId.length < 1)
      .map(cgp => {
        result['-1'] = 'Please select the security group.';
      });
    if (
      value.companyGroupProjects.filter(cgp => cgp.isAdmin && (!cgp.projects || cgp.projects.length < 1)).length > 0 &&
      value.companyGroupProjects.length > 1
    ) {
      value.companyGroupProjects
        .filter(cgp => !cgp.isAdmin)
        .map(cgp => {
          result[cgp.groupId && cgp.groupId.length > 0 ? cgp.groupId : '-1'] =
            'Please remove this group because you already added all projects with admin permission.';
        });
    }
    if (result && Object.keys(result).length > 0) {
      return result;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

export const validateCompanyUserImpersonateAccessList = ({ value }) => {
  if (value && value.length > 0) {
    let result = {};
    value.map(cuia => {
      let cid = cuia.companyId && cuia.companyId.length > 0 ? cuia.companyId : '-1';
      if (cid === '-1') {
        result[cid] = {};
        result[cid].clientId = 'Please select the company.';
      }
      if (cuia.companyGroupProjects && cuia.companyGroupProjects.length) {
        cuia.companyGroupProjects
          .filter(cgp => !cgp.groupId || cgp.groupId.length < 1)
          .map(cgp => {
            if (!result[cid]) {
              result[cid] = {};
            }
            result[cid]['-1'] = 'Please select the security group.';
          });
        if (
          cuia.companyGroupProjects.filter(cgp => cgp.isAdmin && (!cgp.projects || cgp.projects.length < 1)).length > 0 &&
          cuia.companyGroupProjects.length > 1
        ) {
          cuia.companyGroupProjects
            .filter(cgp => !cgp.isAdmin)
            .map(cgp => {
              if (!result[cid]) {
                result[cid] = {};
              }
              result[cid][cgp.groupId && cgp.groupId.length > 0 ? cgp.groupId : '-1'] =
                'Please remove this group because you already added all projects with admin permission.';
            });
        }
      }
    });
    if (result && Object.keys(result).length > 0) {
      return result;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

const defaultUserValueList = [UserFields.PREFERRED_CONTACT_METHOD, UserFields.INVITATION_TYPE];

export const isUserEmpty = user => {
  return Object.entries(user).filter(([field, value]) => !defaultUserValueList.includes(field as UserFields) && !isEmpty(value)).length === 0;
};

const isDuplicatedEmail = (user: IUser, userList: IUser[]) => userList.filter(userItem => userItem.email === user.email).length > 0;

const validateUserList = ({ value: userList, model }: { value: IUser[]; model: any }) => {
  let userErrorList = [];
  if (
    userList.length === 1 &&
    (model.status === ResourceModel.CompanyStatus.DRAFT || model.status === ResourceModel.CompanyStatus.PENDING_APPROVAL) &&
    model.companyType === CompanyType.JointVenture &&
    model.impersonatedAdminUsers.length > 0 &&
    !userList?.[0]?.id &&
    !userList?.[0]?.firstName &&
    !userList?.[0]?.lastName &&
    !userList?.[0]?.email &&
    !userList?.[0]?.title &&
    !userList?.[0]?.mobilePhoneNumber &&
    !userList?.[0]?.isWorker &&
    !userList?.[0]?.groupIds[0] &&
    !userList?.[0]?.projectRoleId
  ) {
    userErrorList = [];
  } else {
    userErrorList = userList.reduce((acc: any, user, index) => {
      const err = validateRules({ model: user, constraints: userRules });
      const isDuplicated =
        !isEmpty(user.email) &&
        isDuplicatedEmail(
          user,
          model.users.filter((u, i) => i !== index)
        );
      if (Object.keys(err).length > 0) {
        Object.entries(err).forEach(([field, value]) => {
          acc[`users[${index}].${field}`] = value;
        });
      }
      if (isDuplicated) {
        acc[`users[${index}].email`] = 'The same Email Address cannot be used in different users.';
      }
      return acc;
    }, {});
  }
  return Object.keys(userErrorList).length > 0 ? userErrorList : false;
};

export const validateRelationshipName = ({ value }: { value: string }) => {
  return !value || value.indexOf(',') > 0 ? false : 'Please enter the Relationship Name with "," separated like (Branch, Sub Branch).';
};

export const validateJointVentureCompanies = ({ value: jointVentureCompanies, model }: { value: IUser[]; model: any }) => {
  return model.companyType === CompanyType.JointVenture
    ? jointVentureCompanies?.length > 0
      ? false
      : 'A joint venture must have at least one company.'
    : false;
};

export const draftFieldRules = (companyType: CompanyType) => ({
  name: {
    required: true,
    rules: [],
  },
  reportsTo: {
    required: false,
    rules: [],
  },
  relationshipName: {
    required: false,
    rules: [validateRelationshipName],
  },
  taxpayerIdentificationNumber: {
    required: false,
    rules: [ruleMap.isValidTaxpayerIdentificationNumber],
    args: [{ max: 12, min: 9 }],
  },
  mwbeTypeId: {
    required: false,
    rules: [],
  },
  trades: {
    required: false,
    rules: [],
  },
  otherTrade: {
    required: false,
    rules: [],
  },
  billingAddress: {
    required: false,
    rules: [validateAddress],
  },
  mailingAddress: {
    required: false,
    rules: [validateAddress],
  },
  users: {
    required: false,
    rules: [validateUserList],
  },
  isUniversalBadge: {
    required: false,
    rules: [],
  },
  universalBadgePrice: {
    required: false,
    rules: [validateUBadgePrice],
  },
  jointVentureCompanies: {
    required: companyType === CompanyType.JointVenture,
    rules: [validateJointVentureCompanies],
  },
  phoneNumber: {
    required: false,
    rules: [({ value }) => value && !isValidNumber(value) && 'Please enter a valid Main Phone Number.'],
  },
});

export const getOnboardingFieldRules = (
  mailingAddressMatchesBillingAddress: boolean,
  currentStep: GeneralModel.IStep,
  hasTrades: boolean,
  otherTrade: string,
  isDeveloper: boolean
): IFormRules => {
  const onGeneralStep = currentStep.key === ClientModel.ClientStep.GENERAL_INFORMATION;
  const onAddressStep = currentStep.key === ClientModel.ClientStep.ADDRESSES;

  return {
    name: {
      required: true,
      rules: [],
    },
    taxpayerIdentificationNumber: {
      required: !isDeveloper && onGeneralStep,
      rules: [ruleMap.isValidTaxpayerIdentificationNumber],
      args: [{ max: 12, min: 9 }],
    },
    mwbeTypeId: {
      required: !isDeveloper && onGeneralStep,
      rules: [],
    },
    trades: {
      required: !isDeveloper && onGeneralStep && !hasTrades && isNull(otherTrade),
      rules: [],
    },
    otherTrade: {
      required: !isDeveloper && onGeneralStep && !isNull(otherTrade),
      rules: [],
    },
    billingAddress: {
      required: onAddressStep,
      rules: [onAddressStep ? validateRequiredAddress : validateAddress],
    },
    mailingAddress: {
      required: onAddressStep && !mailingAddressMatchesBillingAddress,
      rules: [mailingAddressMatchesBillingAddress ? validateAddress : validateRequiredAddress],
    },
    users: {
      required: false,
      rules: [validateUserList],
    },
    isUniversalBadge: {
      required: false,
      rules: [],
    },
    universalBadgePrice: {
      required: false,
      rules: [validateUBadgePrice],
    },
  };
};

export const getGeneralInformationRules = (
  hasTrades: boolean,
  otherTrade: string,
  isDeveloper: boolean,
  hasUniversalBadge: boolean,
  companyType?: ClientModel.CompanyType,
  isVerified?: boolean
) => {
  return {
    ...draftFieldRules,
    name: {
      required: true,
      rules: [],
    },
    reportsTo: {
      required: companyType === ClientModel.CompanyType.Branch,
      rules: [],
    },
    relationshipName: {
      required: companyType === ClientModel.CompanyType.Branch || companyType === ClientModel.CompanyType.HeadOffice,
      rules: [validateRelationshipName],
    },
    taxpayerIdentificationNumber: {
      required: isVerified ? !isDeveloper : false,
      rules: [ruleMap.isValidTaxpayerIdentificationNumber],
      args: [{ max: 12, min: 9 }],
    },
    mwbeTypeId: {
      required: isVerified ? !isDeveloper : false,
      rules: [],
    },
    trades: {
      required: (isVerified ? !isDeveloper : false) && !hasTrades && isNull(otherTrade),
      rules: [],
    },
    otherTrade: {
      required: !isNull(otherTrade),
      rules: [],
    },
    universalBadgePrice: {
      required: hasUniversalBadge,
      rules: [validateUBadgePrice],
    },
    jointVentureCompanies: {
      rules: [validateJointVentureCompanies],
      required: companyType === ClientModel.CompanyType.JointVenture,
    },
  };
};

export const getAddressesRules = (mailingMatchesBilling: boolean, isDeveloper: boolean) => ({
  ...draftFieldRules,
  billingAddress: {
    required: !isDeveloper,
    rules: [validateRequiredAddress],
  },
  mailingAddress: {
    required: !isDeveloper && !mailingMatchesBilling,
    rules: [mailingMatchesBilling ? validateAddress : validateRequiredAddress],
  },
  phoneNumber: {
    required: false,
    rules: [({ value }) => value && !isValidNumber(value) && 'Please enter a valid Main Phone Number.'],
  },
});

export const getConsentFormFieldRules = () => ({
  ...draftFieldRules,
});

export const inviteClientRules: IFormRules = {
  name: {
    required: true,
    rules: [],
  },
  reportsTo: {
    required: false,
    rules: [],
  },
  taxpayerIdentificationNumber: {
    required: false,
    rules: [ruleMap.isValidTaxpayerIdentificationNumber],
    args: [{ max: 12, min: 9 }],
  },
  users: {
    required: true,
    rules: [
      ({ value: userList }: { value: IUser[] }) => {
        const userErrorList = userList.reduce((acc: any, user, index) => {
          const userErrors = validateObject(user, userActiveRules({ assignClientRequired: false }));
          if (Object.keys(userErrors).length > 0) {
            Object.entries(userErrors).forEach(([field, value]) => {
              acc[`users[${index}].${field}`] = value;
            });
          }
          return acc;
        }, {});
        return Object.keys(userErrorList).length > 0 ? userErrorList : false;
      },
    ],
  },
  // sponsor: {
  //   required: false,
  //   rules: [],
  // },
  // taxCondition: {
  //   required: false,
  //   rules: [],
  // },

  projects: {
    required: false,
    rules: [
      ({ value: projectList }: { value: any }) => {
        const projectErrorList = projectList.reduce((acc: any, project, index) => {
          if (project.projectId && project.projectId.length > 0) {
            if (!project.sponsorId || project.sponsorId.length < 1) {
              acc[`projects[${index}].sponsorId`] = 'Please select Sponsor Company';
            }
            if (!project.taxCondition || project.taxCondition.length < 1) {
              acc[`projects[${index}].taxCondition`] = 'Please select Tax Condition';
            }
          }
          return acc;
        }, {});
        return Object.keys(projectErrorList).length > 0 ? projectErrorList : false;
      },
    ],
  },
};

export const getInviteClientRules = (companyType?: ClientModel.CompanyType) => {
  return {
    ...inviteClientRules,
    reportsTo: {
      required: companyType === ClientModel.CompanyType.Branch,
      rules: [],
    },
  };
};

export const inviteClientRulesForOsr = {
    ...inviteClientRules,
    projects: {
      required: true,
      ignoreIsEmpty: true,
      rules: [
        ({ value: projectList }: { value: any }) => {
          let projectErrorList = {};
          if(projectList?.length > 0){
            projectErrorList = projectList.reduce((acc: any, project, index) => {
              if (!project.projectId) {
                acc[`projects[${index}].projectId`] = 'Please select Project';
              }
              if (!project.sponsorId) {
                acc[`projects[${index}].sponsorId`] = 'Please select Sponsor Client';
              }
              if (!project.taxCondition) {
                acc[`projects[${index}].taxCondition`] = 'Please select Tax Condition';
              }
              return acc;
            }, {});
          } else {
            projectErrorList = {
              'projects[0].projectId': 'Please select Project',
              'projects[0].sponsorId': 'Please select Sponsor Client',
              'projects[0].taxCondition': 'Please select Tax Condition'
            }
          }
          return Object.keys(projectErrorList).length > 0 ? projectErrorList : false;
        },
      ],
    },
};

const validateWorkerProfileFields = ({ value: fields }: { value: IWorkerProfileField[] }) => {
  const errorList = fields.reduce((err: any, field, index) => {
    if (
      field.canOverrideSkippableByDefault &&
      (field.isVisible || (field.isVisible === null && field.isVisibleByDefault)) &&
      // (field.isSkippable || (field.isSkippable === null && field.isSkippableByDefault)) &&
      field.isSkippable &&
      field.skippableTill === 0
    ) {
      err[field.fieldId] = 'Please select the Skippable Until';
    }
    return err;
  }, {});
  return Object.keys(errorList).length > 0 ? errorList : false;
};

export const WorkerProfileFieldsRules = {
  workerCustomProfileFields: {
    required: false,
    rules: [validateWorkerProfileFields],
  },
};

export const isConsentFormFieldListItemsDuplicated = (item: any, items: any[]) => items.filter(element => element.fieldValue === item.fieldValue).length > 0;
